// ─────────────────────────────────────────────────────────────────────────────
// BIZPLUS PARITY — CRM Setup behaviour toggles  (Appendix)
//
// 81 switches that change how the app behaves. Two things this screen is careful
// about:
//
//   · A toggle with nothing reading it yet is MARKED as such. Most of these are
//     in that state today, and presenting all 81 as working switches would be a
//     lie the client only discovers when the behaviour does not change.
//   · The switch reflects SAVED state, not the click. A failed save leaves it
//     where it was rather than showing a setting that was never stored.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { toast } from 'react-hot-toast';
import { SettingsSubNav } from '../components/SettingsSubNav';
import { SlidersHorizontal, Search, Info, CheckCircle2 } from 'lucide-react';

interface Flag {
  key: string; label: string; group: string; description?: string;
  enforced: boolean; value: boolean;
}

export function CrmSetupSettingsPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [showOnlyOn, setShowOnlyOn] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['feature-flags'],
    queryFn: async () => (await api.get('/api/feature-flags')).data,
  });

  const save = useMutation({
    mutationFn: async (values: Record<string, boolean>) =>
      (await api.patch('/api/feature-flags', { values })).data,
    onSuccess: (d: any) => {
      qc.invalidateQueries({ queryKey: ['feature-flags'] });
      if (d.rejected?.length) toast.error(`Not recognised: ${d.rejected.join(', ')}`);
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Could not save.'),
  });

  const flags: Flag[] = data?.flags || [];

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = flags.filter((f) => {
      if (showOnlyOn && !f.value) return false;
      if (!needle) return true;
      return f.label.toLowerCase().includes(needle)
        || f.key.toLowerCase().includes(needle)
        || f.group.toLowerCase().includes(needle);
    });
    const byGroup = new Map<string, Flag[]>();
    for (const f of filtered) byGroup.set(f.group, [...(byGroup.get(f.group) || []), f]);
    return [...byGroup.entries()];
  }, [flags, q, showOnlyOn]);

  const onCount = flags.filter((f) => f.value).length;
  const unenforcedOn: string[] = data?.unenforcedActive || [];

  return (
    <div className="text-slate-900 dark:text-slate-100">
      <SettingsSubNav />

      <div className="p-6 max-w-5xl mx-auto space-y-5">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5 text-primary" /> CRM Setup
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {flags.length} behaviour switches. {onCount} on. Everything is off until you turn it on.
          </p>
        </div>

        {/* Stated once, at the top, rather than leaving the client to work out
            why a switch they set changed nothing. */}
        <div className="p-3 rounded-xl bg-surface-variant/40 border border-outline text-xs flex gap-2">
          <Info className="w-4 h-4 shrink-0 text-primary mt-0.5" />
          <p>
            Switches marked <span className="font-semibold">not yet wired</span> save and persist,
            but nothing reads them yet — turning one on will not change behaviour. They are listed
            so the configuration can be captured now and honoured as each is implemented.
          </p>
        </div>

        {unenforcedOn.length > 0 && (
          <div className="p-3 rounded-xl bg-warning/10 dark:bg-warning border border-warning/25 dark:border-warning/40 text-xs text-warning-text dark:text-warning-text">
            {unenforcedOn.length} switch(es) are on but not yet wired, so they are having no effect:{' '}
            {unenforcedOn.join(', ')}.
          </div>
        )}

        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[14rem]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search settings…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent text-sm" />
          </div>
          <label className="flex items-center gap-2 text-xs font-semibold">
            <input type="checkbox" checked={showOnlyOn} onChange={(e) => setShowOnlyOn(e.target.checked)} />
            Only show what is on
          </label>
        </div>

        {isLoading && <p className="text-sm text-slate-500">Loading…</p>}

        {!isLoading && groups.length === 0 && (
          <p className="text-sm text-slate-500">Nothing matches that search.</p>
        )}

        {groups.map(([group, groupFlags]) => (
          <div key={group} className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="px-4 py-2 bg-slate-100 dark:bg-slate-900 text-xs font-bold uppercase tracking-wide">
              {group}
            </div>
            <div className="divide-y divide-slate-200 dark:divide-slate-800">
              {groupFlags.map((f) => (
                <div key={f.key} className="px-4 py-3 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{f.label}</span>
                      {f.enforced ? (
                        <span className="px-1.5 py-0.5 rounded bg-success/10 dark:bg-success text-success-text dark:text-success-text text-[10px] font-bold flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> active
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-[10px] font-bold">
                          not yet wired
                        </span>
                      )}
                    </div>
                    {f.description && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{f.description}</p>
                    )}
                    <code className="text-[10px] text-slate-400">{f.key}</code>
                  </div>
                  <label className="shrink-0 pt-0.5">
                    {/* Reflects SAVED state. A failed save leaves the switch
                        where it was rather than showing a setting that was
                        never stored. */}
                    <input type="checkbox" checked={f.value} disabled={save.isPending}
                      onChange={(e) => save.mutate({ [f.key]: e.target.checked })} />
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CrmSetupSettingsPage;
